import React from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { LoginPage } from "@src/pages/LoginPage";

interface UnauthenticatedAppProps {
  login: (email: string, password: string) => Promise<void>;
}

/**
 * UnauthenticatedApp - Renders login flow for unauthenticated users
 */
export const UnauthenticatedApp: React.FC<UnauthenticatedAppProps> = ({ login }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const handleLoginSuccess = () => {
    // After successful login, redirect to the original requested path
    // Get the return path from URL search params
    const searchParams = new URLSearchParams(location.search);
    const returnPath = searchParams.get("returnPath") || "/backtests";

    navigate(returnPath, { replace: true });
  };

  // Save the current location as return path (unless we're already on login).
  // Search and hash are kept: a link whose meaning lives in its query, like a shared chart or a
  // filtered list, is a different page once they are dropped, and the user lands somewhere blank
  // with no way to tell what was lost.
  const returnPath =
    location.pathname !== "/login"
      ? `${location.pathname}${location.search}${location.hash}`
      : "/backtests";

  return (
    <Routes>
      <Route
        path="/login"
        element={<LoginPage onLoginSuccess={handleLoginSuccess} login={login} />}
      />
      <Route
        path="*"
        element={<Navigate to={`/login?returnPath=${encodeURIComponent(returnPath)}`} replace />}
      />
    </Routes>
  );
};
